import { useEffect, useState } from 'react'

// Constants matching theme values
const LEFT_MENU_WIDTH_COLLAPSED = 57
const LEFT_MENU_WIDTH_EXPANDED = 290
const MOBILE_BREAKPOINT = 960

/**
 * Hook to track the current left menu width.
 * Reads the width from CSS variable set by LeftMenu component.
 *
 * @returns Current left menu width in pixels
 */
export const useLeftMenuWidth = (): number => {
  const [leftMenuWidth, setLeftMenuWidth] = useState(() => {
    if (typeof window === 'undefined') return LEFT_MENU_WIDTH_COLLAPSED
    if (window.innerWidth < MOBILE_BREAKPOINT) return 0

    const cssWidth =
      document.documentElement.style.getPropertyValue('--left-menu-width')
    if (cssWidth) {
      return Number.parseInt(cssWidth, 10)
    }

    return LEFT_MENU_WIDTH_COLLAPSED
  })

  useEffect(() => {
    const updateMenuWidth = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT

      if (isMobile) {
        setLeftMenuWidth(0)
        return
      }

      const cssWidth =
        document.documentElement.style.getPropertyValue('--left-menu-width')
      if (cssWidth) {
        setLeftMenuWidth(Number.parseInt(cssWidth, 10))
      } else {
        setLeftMenuWidth(LEFT_MENU_WIDTH_COLLAPSED)
      }
    }

    window.addEventListener('resize', updateMenuWidth)

    // MutationObserver to watch for CSS variable changes
    const observer = new MutationObserver(updateMenuWidth)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    })

    return () => {
      window.removeEventListener('resize', updateMenuWidth)
      observer.disconnect()
    }
  }, [])

  return leftMenuWidth
}
